export type SamplePrompt = {
  n: number;
  title: string;
  text: string;
  tips: string[];
};

export type AiTarget = {
  name: string;
  url?: (q: string) => string;
};

export const aiTargets: AiTarget[] = [
  { name: 'ChatGPT', url: (q: string) => `https://chatgpt.com/?q=${encodeURIComponent(q)}` },
  { name: 'Claude', url: (q: string) => `https://claude.ai/new?q=${encodeURIComponent(q)}` },
  { name: 'Gemini', url: () => 'https://gemini.google.com/app' },
  { name: 'Other Compatible AI' },
];

export const demoPrompt = `Act as an electrical engineering calculation review assistant. Help me organize and review the information provided for the following calculation. Do not invent missing values or assume unspecified parameters are known.

Project: [PROJECT DESCRIPTION]
System configuration: [SYSTEM CONFIGURATION]
Supply voltage and units: [VOLTAGE AND UNITS]
Load information: [LOAD DATA]
Conductor information: [CONDUCTOR MATERIAL, SIZE, LENGTH, AND RELEVANT CONDITIONS]
Applicable design criteria and standards: [SPECIFY THE VERIFIED REQUIREMENTS]
Calculation objective: [DESCRIBE THE CALCULATION]

Organize your response into the following sections:
1. Information supplied and missing inputs.
2. Assumptions requiring confirmation.
3. Relevant formulas and units.
4. Calculation steps, with inputs and intermediate results clearly identified.
5. Potential inconsistencies or limitations.
6. Independent verification steps and references that a qualified engineer should consult.

If essential information is missing, ask clarifying questions before proceeding. Do not claim that a result is compliant, safe, or approved without sufficient evidence and appropriate verification.`;

export const demoOutput = {
  title: 'CALCULATION REVIEW — PRELIMINARY OUTPUT',
  sections: [
    { heading: 'Inputs', lines: ['A structured summary of the information provided, including the values and conditions that still need confirmation.'] },
    { heading: 'Assumptions', lines: ['A list of explicit assumptions, unresolved variables, and conditions that may affect the calculation.'] },
    { heading: 'Calculation Steps', lines: ['An organized presentation of relevant formulas, units, substitutions, and intermediate results where sufficient information is available.'] },
    { heading: 'Review Points', lines: ['A list of potential inconsistencies, missing information, and questions requiring further investigation.'] },
    { heading: 'Independent Verification', lines: ['A checklist of calculations, source references, design criteria, and technical requirements that a qualified professional should verify before relying on the result.'] },
  ],
};

export const samplePrompts: SamplePrompt[] = [
  {
    n: 1,
    title: 'Electrical Design Requirements Review',
    text: `Act as an electrical engineering assistant specializing in the organization and review of electrical design requirements.

Your task is to help prepare a structured preliminary design requirements document using the information supplied by the user.

Begin by establishing the project context:
[PROJECT DESCRIPTION]
[PROJECT LOCATION AND APPLICABLE JURISDICTION]
[SYSTEM CONFIGURATION]
[SUPPLY CHARACTERISTICS]
[LOAD INFORMATION]
[EQUIPMENT AND INTERFACE REQUIREMENTS]
[APPLICABLE DESIGN STANDARDS AND EDITIONS]
[PROJECT-SPECIFIC CONSTRAINTS]

Organize the available information into clearly defined sections covering project objectives, known technical inputs, design constraints, missing information, assumptions requiring confirmation, and questions that must be resolved before proceeding.

Distinguish verified information from assumptions and unresolved questions. Do not invent equipment specifications, measurements, code requirements, or manufacturer data.

Where applicable, identify which information must be confirmed against authoritative sources and which decisions require qualified engineering review.

Present the result as a structured preliminary design requirements document that can be reviewed and adapted by the responsible engineer.

If essential information is missing, ask the necessary clarifying questions before making assumptions.`,
    tips: [
      'Verify that the project information and system configuration are accurate.',
      'Check that supplied values include appropriate units and that missing parameters have been identified.',
      'Confirm the applicable jurisdiction, standards, and project requirements.',
      'Review all assumptions and technical statements before using the draft in a real engineering project.',
    ],
  },
  {
    n: 2,
    title: 'Electrical Calculation Review',
    text: demoPrompt,
    tips: [
      'Confirm every value has the correct unit and source before sending.',
      'Check that the design criteria and standard editions you list are the verified ones for this project.',
      'Independently verify all results before relying on them.',
    ],
  },
  {
    n: 3,
    title: 'Electrical Fault Investigation',
    text: `Act as an electrical troubleshooting assistant specializing in structured fault investigation. Help me organize the available information about the following electrical fault so it can be reviewed by a qualified professional.

Equipment or system affected: [EQUIPMENT OR SYSTEM DESCRIPTION]
Observed symptoms: [SYMPTOMS OBSERVED]
Operating conditions when the fault occurred: [OPERATING CONDITIONS]
Recent changes, maintenance, or events: [RECENT CHANGES OR EVENTS]
Measurements and diagnostic evidence available: [MEASUREMENTS AND EVIDENCE, WITH UNITS]
Applicable safety procedures and site requirements: [SAFETY PROCEDURES]

Organize your response into the following sections:
1. Summary of the reported fault and supplied evidence.
2. Missing information and additional data that should be collected.
3. Potential causes, ranked by how well they are supported by the evidence, with the reasoning for each.
4. Diagnostic checks that could confirm or rule out each potential cause.
5. A corrective-action record template for documenting findings and actions taken.

Do not invent measurements or equipment data. Clearly distinguish evidence from hypotheses. Do not provide instructions that bypass applicable safety procedures, and remind me that all work on electrical equipment must follow the site's safety requirements and be performed by qualified personnel.

If essential information is missing, ask clarifying questions before proceeding.`,
    tips: [
      'Record symptoms and measurements exactly as observed, including units and time of occurrence.',
      'Follow all applicable safety procedures before performing any diagnostic check.',
      'Have potential causes and corrective actions reviewed by qualified personnel.',
    ],
  },
];
